import { createContext, useContext } from 'react';
import { createStore, useStore } from 'zustand';
import type { StoreApi } from 'zustand';
import type { Box, Side, Viewport, XY } from './geometry';
import { GROUP_BAR } from './groups';

export interface NodeBox extends Box {
  measured: boolean;
  /** A group's size comes from the server and the corner handle, never from the ResizeObserver. */
  group: boolean;
}

type Size = { width: number; height: number };

export interface Connecting {
  sourceId: string;
  /** The handle the drag started on. A left handle makes the origin the TARGET. */
  fromSide: Side;
  /** The pointer, in flow units. */
  pointer: XY;
  /** The handle the line snaps to, when there is one. */
  target: { nodeId: string; side: Side } | null;
  /** null: not near a handle. */
  valid: boolean | null;
}

export interface CanvasState {
  /** Mirrored at gesture end only. The live value is `CanvasRuntime.vp`. */
  viewport: Viewport;
  nodes: Record<string, NodeBox>;
  selection: Set<string>;
  edgeSelection: Set<string>;
  connecting: Connecting | null;
  /** Pane-relative screen rect. */
  marquee: Box | null;
  /** The groups the dragged roots would join if dropped now; a drop that changes nothing adds none. */
  dropTargets: Set<string>;

  /**
   * Positions from the server, plus every group's size, applied only where they
   * changed since last seen. A node absent from `groupSizes` is a topic.
   */
  syncPositions: (positions: Record<string, XY>, groupSizes?: Record<string, Size>) => void;
  moveNodes: (positions: Record<string, XY>) => void;
  setSizes: (sizes: Record<string, Size>) => void;
  /** A group's live size under the corner handle. */
  resizeNode: (id: string, size: Size) => void;
  setDropTargets: (ids: Set<string>) => void;
  selectNodes: (ids: Iterable<string>) => void;
  selectEdges: (ids: Iterable<string>) => void;
  clearSelection: () => void;
  pruneEdges: (liveIds: Set<string>) => void;
}

const sameSet = (a: Set<string>, b: Set<string>) => a.size === b.size && Array.from(a).every((id) => b.has(id));

export const createCanvasStore = () => {
  // Last positions the server sent, and the ids a gesture is moving right now.
  // Neither is rendered, so they live outside the state.
  const server: Record<string, XY> = {};
  const serverSizes: Record<string, Size> = {};
  const moving = new Set<string>();

  const store = createStore<CanvasState>()((set, get) => ({
    viewport: { x: 0, y: 0, zoom: 1 },
    nodes: {},
    selection: new Set(),
    edgeSelection: new Set(),
    connecting: null,
    marquee: null,
    dropTargets: new Set(),

    syncPositions: (positions, groupSizes = {}) => {
      const prev = get().nodes;
      const next: Record<string, NodeBox> = {};
      let changed = Object.keys(prev).length !== Object.keys(positions).length;
      Object.keys(positions).forEach((id) => {
        const p = positions[id];
        const size = groupSizes[id];
        const box = prev[id];
        const seen = server[id];
        const seenSize = serverSizes[id];
        server[id] = p;
        if (size) serverSizes[id] = size;
        if (!box) {
          next[id] = size
            ? { x: p.x, y: p.y, ...size, handleY: GROUP_BAR / 2, measured: true, group: true }
            : { x: p.x, y: p.y, width: 0, height: 0, measured: false, group: false };
          changed = true;
          return;
        }
        let nextBox = box;
        if (!moving.has(id) && (!seen || seen.x !== p.x || seen.y !== p.y) && (box.x !== p.x || box.y !== p.y)) {
          nextBox = { ...nextBox, x: p.x, y: p.y };
        }
        if (
          size &&
          !moving.has(id) &&
          (!seenSize || seenSize.width !== size.width || seenSize.height !== size.height) &&
          (box.width !== size.width || box.height !== size.height)
        ) {
          nextBox = { ...nextBox, ...size };
        }
        // Trap 7, per-node subscriptions: only a node whose box changed gets a new object.
        if (nextBox !== box) changed = true;
        next[id] = nextBox;
      });
      Object.keys(server).forEach((id) => {
        if (!positions[id]) delete server[id];
      });
      Object.keys(serverSizes).forEach((id) => {
        if (!groupSizes[id]) delete serverSizes[id];
      });
      if (!changed) return;
      const { selection } = get();
      const kept = new Set(Array.from(selection).filter((id) => next[id]));
      set({ nodes: next, selection: kept.size === selection.size ? selection : kept });
    },

    moveNodes: (positions) => {
      const nodes = { ...get().nodes };
      Object.keys(positions).forEach((id) => {
        const box = nodes[id];
        if (box) nodes[id] = { ...box, x: positions[id].x, y: positions[id].y };
      });
      set({ nodes });
    },

    setSizes: (sizes) => {
      const prev = get().nodes;
      let nodes: Record<string, NodeBox> | null = null;
      Object.keys(sizes).forEach((id) => {
        const box = prev[id];
        const s = sizes[id];
        if (!box || box.group || (box.measured && box.width === s.width && box.height === s.height)) return;
        if (!nodes) nodes = { ...prev };
        nodes[id] = { ...box, width: s.width, height: s.height, measured: true };
      });
      if (nodes) set({ nodes });
    },

    resizeNode: (id, size) => {
      const box = get().nodes[id];
      if (!box || (box.width === size.width && box.height === size.height)) return;
      set({ nodes: { ...get().nodes, [id]: { ...box, width: size.width, height: size.height } } });
    },

    setDropTargets: (ids) => {
      if (!sameSet(ids, get().dropTargets)) set({ dropTargets: ids });
    },

    selectNodes: (ids) => {
      const selection = new Set(ids);
      const s = get();
      if (sameSet(selection, s.selection) && s.edgeSelection.size === 0) return;
      set({ selection, edgeSelection: new Set() });
    },

    selectEdges: (ids) => {
      const edgeSelection = new Set(ids);
      const s = get();
      if (sameSet(edgeSelection, s.edgeSelection) && s.selection.size === 0) return;
      set({ edgeSelection, selection: new Set() });
    },

    clearSelection: () => {
      const s = get();
      if (s.selection.size === 0 && s.edgeSelection.size === 0) return;
      set({ selection: new Set(), edgeSelection: new Set() });
    },

    pruneEdges: (liveIds) => {
      const { edgeSelection } = get();
      const kept = new Set(Array.from(edgeSelection).filter((id) => liveIds.has(id)));
      if (kept.size !== edgeSelection.size) set({ edgeSelection: kept });
    },
  }));

  return {
    store,
    /** Marks nodes as mid-gesture so a reload landing now cannot yank them back. */
    beginMove: (ids: Iterable<string>) => Array.from(ids).forEach((id) => moving.add(id)),
    endMove: (ids: Iterable<string>) => Array.from(ids).forEach((id) => moving.delete(id)),
    /** The last server position, for putting a node back after a failed save. */
    serverPosition: (id: string): XY | undefined => server[id],
    /** The last server size of a group, for putting it back after a failed save. */
    serverSize: (id: string): Size | undefined => serverSizes[id],
  };
};

export type CanvasStoreBundle = ReturnType<typeof createCanvasStore>;
export type CanvasStore = StoreApi<CanvasState>;

export const CanvasStoreContext = createContext<CanvasStoreBundle | null>(null);

export const useCanvasBundle = (): CanvasStoreBundle => {
  const bundle = useContext(CanvasStoreContext);
  if (!bundle) throw new Error('Canvas store used outside <Canvas>.');
  return bundle;
};

export function useCanvas<U>(selector: (s: CanvasState) => U): U {
  return useStore(useCanvasBundle().store, selector);
}

/** One node's box: the per-node subscription that keeps a drag to one re-render. */
export const useNodeBox = (id: string) => useCanvas((s) => s.nodes[id]);
